import {
  DraftConfidence,
  DraftRunStatus,
  IDraftAttribution,
  IDraftRecMatch,
  IDraftRecommendation,
} from "store/queries/contractModelDrafts";

/** Upload types clear-api accepts (mirrors ALLOWED_EXTENSIONS in the backend service). */
export const ALLOWED_UPLOAD_EXTENSIONS = [
  "pdf",
  "xlsx",
  "xls",
  "csv",
  "docx",
  "doc",
  "txt",
];

/** Display label for a run: its name, or a short id fallback. */
export const runDisplayName = (run: {
  name: string | null;
  id: string;
}): string => run.name?.trim() || `Run ${run.id.slice(0, 8)}`;

/** Format a run timestamp for the list table. */
export const formatRunDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

/** Statuses where the model is being produced (worker claimed / running / returned, awaiting persist). */
export const PROCESSING_STATUSES: DraftRunStatus[] = [
  "queued",
  "running",
  "worker_returned",
];

export const isProcessing = (status: DraftRunStatus): boolean =>
  PROCESSING_STATUSES.includes(status);

/** Friendly, human-facing label for a run status. */
export const runStatusLabel = (status: DraftRunStatus): string => {
  switch (status) {
    case "collecting":
      return "Draft";
    case "queued":
    case "running":
    case "worker_returned":
      return "Generating";
    case "awaiting_input":
      return "Needs input";
    case "completed":
      return "Completed";
    case "failed":
    case "dead":
      return "Failed";
    case "canceled":
      return "Canceled";
    default:
      return status;
  }
};

/**
 * Helper text shown next to the Generate button, or null when it should just be enabled.
 * Ordered by precedence: the reason the button is disabled *right now* wins.
 */
export const generateHint = (
  documentCount: number,
  status: DraftRunStatus,
  opts: { uploading?: boolean; finished?: boolean } = {}
): string | null => {
  if (opts.uploading) return "Wait for the upload to finish before generating.";
  // A finished run is no longer a dead end: documents can be added and the model re-derived into a new
  // version, with the reviewer's verdicts carried forward. Saying "start a new run" would send her off
  // to throw away work she doesn't need to lose.
  if (opts.finished)
    return "Already generated. Add a document or an instruction, then Regenerate — your confirmations are kept.";
  if (documentCount === 0) return "Upload at least one document to generate.";
  if (isProcessing(status))
    return "The model is analyzing your documents — this can take a few minutes.";
  return null;
};

/** PrimeReact Tag severity for a run status. */
export const runStatusSeverity = (
  status: DraftRunStatus
): "success" | "info" | "warning" | "danger" | undefined => {
  switch (status) {
    case "completed":
      return "success";
    case "queued":
    case "running":
    case "worker_returned":
      return "info";
    case "awaiting_input":
      return "warning";
    case "failed":
    case "dead":
      return "danger";
    default:
      return undefined; // collecting, canceled → neutral
  }
};

/** Map a confidence bucket to a PrimeReact Tag severity. */
export const confidenceSeverity = (
  c: DraftConfidence
): "success" | "warning" | "danger" => {
  if (c === "High") return "success";
  if (c === "Medium") return "warning";
  return "danger";
};

/** Group recommendations by componentType for sectioned display. */
/** The order a human would actually build these in — files/terms first, then rates, then rulesets. */
const IMPLEMENTATION_ORDER: string[] = [
  "contract_term",
  "base_rate_file",
  "rate_calculation",
  "carveout",
  "case_rate_ruleset",
  "package_ruleset",
  "inpatient_ruleset",
  "exploding_fee_schedule",
  "mpdr_ruleset",
  "lesser_of_charges",
  "charge_based_pricing",
  "ma_payer_link",
  "provider_decomposition",
  "payer_consolidation",
];

/** Recommendations ordered by build sequence (then sortOrder) — files/terms first, then rates, then rulesets. */
export const forChecklist = (
  recs: IDraftRecommendation[]
): IDraftRecommendation[] => {
  const rank = (t: string) => {
    const i = IMPLEMENTATION_ORDER.indexOf(t);
    return i === -1 ? IMPLEMENTATION_ORDER.length : i;
  };
  return [...recs].sort(
    (a, b) =>
      rank(a.componentType) - rank(b.componentType) || a.sortOrder - b.sortOrder
  );
};

export const groupByComponentType = (
  recs: IDraftRecommendation[]
): Record<string, IDraftRecommendation[]> => {
  // Group in the same build order the implementation checklist uses (terms/files first, then rates,
  // then rulesets) so the recommendation cards read top-to-bottom the way a human would build them,
  // not in generation order. Component types are non-numeric keys, so insertion order is preserved.
  const byType: Record<string, IDraftRecommendation[]> = {};
  forChecklist(recs).forEach((r) => {
    byType[r.componentType] = byType[r.componentType] || [];
    byType[r.componentType].push(r);
  });
  return byType;
};

/** Coerce a model-supplied string array (missingInputs / openQuestions) into clean strings. */
const stringList = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.map((m) => String(m).trim()).filter((m) => m.length > 0)
    : [];

/** Advisory items a human should confirm. These never block a run. */
export const openQuestionsOf = (rec: IDraftRecommendation): string[] =>
  stringList(rec.openQuestions);

/** BLOCKERS: inputs without which this component cannot be modeled at all. */
export const missingInputsOf = (rec: IDraftRecommendation): string[] =>
  stringList(rec.missingInputs);

/** Every distinct thing the model said it still needs, across the whole run. */
export const collectMissingInputs = (
  recs: IDraftRecommendation[]
): string[] => {
  const seen = new Set<string>();
  recs.forEach((rec) => missingInputsOf(rec).forEach((m) => seen.add(m)));
  return [...seen];
};

// Heuristic: does this missing input read like a document the user should upload?
const DOCUMENT_HINTS = [
  "agreement",
  "amendment",
  "exhibit",
  "addendum",
  "attachment",
  "schedule",
  "document",
  "file",
  "not included",
  "not provided",
  "upload",
];

export const looksLikeMissingDocument = (input: string): boolean => {
  const s = input.toLowerCase();
  return DOCUMENT_HINTS.some((hint) => s.includes(hint));
};

/**
 * A run is "finished" once it has produced recommendations. Re-generating or adding documents then
 * would append a second set of recommendations to the same version, so those actions are locked.
 */
export const isRunFinished = (recs: IDraftRecommendation[]): boolean =>
  recs.length > 0;

/** A recommendation is unverified-sourced if it has attributions but none verified. */
export const hasUnverifiedAttribution = (rec: IDraftRecommendation): boolean =>
  rec.attributions.length > 0 && rec.attributions.every((a) => !a.verified);

/**
 * How much a source quote is actually worth.
 *
 * `verified` means the quote was matched against text WE extracted from the file. A quote that merely
 * matched the model's own reported chunk proves self-consistency, not that the span is in the contract
 * — so it gets its own, deliberately unreassuring badge rather than the green one.
 */
export const attributionBadge = (
  a: Pick<IDraftAttribution, "verified" | "verificationMethod">
): { label: string; severity: "success" | "warning" | "danger" } => {
  if (a.verified)
    return {
      label: `verified against document (${a.verificationMethod})`,
      severity: "success",
    };
  if (a.verificationMethod && a.verificationMethod !== "unverified")
    return {
      label: "quoted by the model — not independently verified",
      severity: "warning",
    };
  return { label: "unverified", severity: "danger" };
};

/** Human labels for the componentType enum — the snake_case values read as too technical in the UI. */
const COMPONENT_TYPE_LABELS: Record<string, string> = {
  provider_decomposition: "Provider Decomposition",
  payer_consolidation: "Payer Consolidation",
  contract_term: "Contract Term",
  base_rate_file: "Base Rate File",
  rate_calculation: "Rate Calculation",
  carveout: "Carveout",
  case_rate_ruleset: "Case-Rate Ruleset",
  inpatient_ruleset: "Inpatient Ruleset",
  package_ruleset: "Package Ruleset",
  exploding_fee_schedule: "Exploding Fee Schedule",
  mpdr_ruleset: "Multiple-Procedure Discount",
  lesser_of_charges: "Lesser-of-Charges",
  charge_based_pricing: "Charge-Based Pricing",
  ma_payer_link: "Medicare Advantage Link",
};

export const componentTypeLabel = (type: string): string =>
  COMPONENT_TYPE_LABELS[type] ?? type.replace(/_/g, " ");

/** Human label for the rate tier a run is scoped to. */
export const siteTierLabel = (tier: string | null): string | null => {
  if (tier === "non_facility") return "Office / Non-Facility";
  if (tier === "facility") return "Facility / Outpatient";
  if (tier === "both") return "Both tiers";
  return null;
};

/** Turn a proposedModeling key ("ratePercent", "cptCode") into a readable label ("Rate Percent", "CPT Code"). */
export const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase → words
    .replace(/_/g, " ")
    .replace(/\bcpt\b/gi, "CPT")
    .replace(/\bid\b/gi, "ID")
    .replace(/^\w/, (c) => c.toUpperCase());

const asNum = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)))
    return Number(v);
  return null;
};

/** Case-insensitive lookup of the first present key among `keys` on a plain object. */
const lookup = (obj: unknown, ...keys: string[]): unknown => {
  if (!obj || typeof obj !== "object") return undefined;
  const lower = keys.map((k) => k.toLowerCase());
  const found = Object.entries(obj as Record<string, unknown>).find(([k]) =>
    lower.includes(k.toLowerCase())
  );
  return found ? found[1] : undefined;
};

/** The one scalar we can compactly compare for a component type ("102%", "0.85× charges"), else null. */
const scalarLabel = (componentType: string, obj: unknown): string | null => {
  if (componentType === "rate_calculation") {
    const pct = asNum(lookup(obj, "ratePercent"));
    return pct != null ? `${pct}%` : null;
  }
  if (componentType === "lesser_of_charges") {
    const m = asNum(lookup(obj, "multiplier", "capMultiplier"));
    return m != null ? `${m}× charges` : null;
  }
  return null;
};

export interface IComparisonSummary {
  proposed: string | null;
  production: string | null;
}

/**
 * A compact "proposed X vs production Y" pair for the checklist's production column — for the value-
 * comparable types (rate-calc %, lesser-of multiplier). Types whose values live elsewhere (carveout CSVs,
 * rulesets we don't value-diff) return nulls; those are judged on the recommendation card below.
 */
export const comparisonSummary = (
  rec: IDraftRecommendation,
  match: IDraftRecMatch | null | undefined
): IComparisonSummary => ({
  proposed: scalarLabel(rec.componentType, rec.proposedModeling),
  production:
    match && match.rows.length > 0
      ? scalarLabel(rec.componentType, match.rows[0])
      : null,
});

const strOf = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v : null;

const pmObj = (rec: IDraftRecommendation): Record<string, unknown> =>
  rec.proposedModeling && typeof rec.proposedModeling === "object"
    ? (rec.proposedModeling as Record<string, unknown>)
    : {};

/** The payer a recommendation is for, from its proposedModeling. Payer-agnostic recs (base files, terms) have none. */
export const recPayer = (
  rec: IDraftRecommendation
): { id: number | null; name: string | null } => {
  const p = pmObj(rec);
  return {
    id: asNum(lookup(p, "payerId")),
    name: strOf(lookup(p, "payerName")),
  };
};

/**
 * The one line that distinguishes otherwise-identical rows of the same type (e.g. three rate calcs) —
 * the rate/file, multiplier, or ruleset group. Payer is shown separately (rows are grouped by payer).
 */
export const recSubtitle = (rec: IDraftRecommendation): string | null => {
  const p = pmObj(rec);
  const file = strOf(lookup(p, "baseRateFileDisplayName", "baseRateFile"));
  const group = strOf(lookup(p, "groupName"));
  if (rec.componentType === "rate_calculation") {
    return (
      [scalarLabel("rate_calculation", p), file].filter(Boolean).join(" · ") ||
      null
    );
  }
  if (rec.componentType === "base_rate_file") return file;
  if (rec.componentType === "lesser_of_charges")
    return scalarLabel("lesser_of_charges", p);
  return group || file || null;
};

const RULESET_EDIT_SEGMENT: Record<string, string> = {
  lesser_of_charges: "lesser-of-charges",
  mpdr_ruleset: "mppr",
  case_rate_ruleset: "case-rate",
  package_ruleset: "packages",
  inpatient_ruleset: "inpatient-rulesets",
  exploding_fee_schedule: "exploding-fee-schedules",
  charge_based_pricing: "charge-based-pricing",
};

/** Live-row keys that are link metadata, not values to display. */
export const LIVE_META_KEYS = ["linkEditId", "linkCfsId"];

/**
 * A deep link into the Payer Rules editor for a live production row, or null if that construct has no
 * navigable page. Rulesets link to their own edit page; contract terms / rate calcs / carveouts / base
 * rate files link to the contract fee schedule page they live on (there's no per-item route for those).
 * Relies on the linkEditId / linkCfsId metadata the live-model reader attaches to each row.
 */
export const payerRulesHref = (
  componentType: string,
  row: Record<string, unknown> | null | undefined
): string | null => {
  if (!row) return null;
  const editId = typeof row.linkEditId === "string" ? row.linkEditId : null;
  const cfsId = typeof row.linkCfsId === "string" ? row.linkCfsId : null;
  const seg = RULESET_EDIT_SEGMENT[componentType];
  if (seg && editId) return `/payer-rules/${seg}/edit/${editId}`;
  if (
    cfsId &&
    (componentType === "contract_term" ||
      componentType === "rate_calculation" ||
      componentType === "carveout" ||
      componentType === "base_rate_file")
  )
    return `/payer-rules/contracts/${cfsId}`;
  return null;
};

/** The first deep link among a recommendation's matched production rows, or null. */
export const firstPayerRulesHref = (
  componentType: string,
  rows: Record<string, unknown>[] | null | undefined
): string | null => {
  if (!rows) return null;
  const hit = rows.map((r) => payerRulesHref(componentType, r)).find(Boolean);
  return hit ?? null;
};

/**
 * Can the tool auto-generate this base_rate_file's CSV today? Mirrors the backend `medicarePhysicianSpec`
 * check — only a "% of Medicare" physician fee schedule (facility/non-facility) with a locality GPCI. The
 * backend is the source of truth; this just decides whether to show the "Generate CSV" button.
 */
export const canGenerateBaseRateFile = (rec: IDraftRecommendation): boolean => {
  if (rec.componentType !== "base_rate_file") return false;
  const pm = pmObj(rec);
  const gen = (
    pm.baseRateFileGeneration && typeof pm.baseRateFileGeneration === "object"
      ? pm.baseRateFileGeneration
      : pm
  ) as Record<string, unknown>;
  const feeType = String(gen.feeScheduleType ?? "").toUpperCase();
  if (feeType !== "PHYSICIAN_FACILITY" && feeType !== "PHYSICIAN_NON_FACILITY")
    return false;
  const gpci = (
    gen.gpci && typeof gen.gpci === "object" ? gen.gpci : gen
  ) as Record<string, unknown>;
  const hasGpci = [
    gpci.pw ?? gen.gpciPw,
    gpci.pe ?? gen.gpciPe,
    gpci.mp ?? gen.gpciMp,
  ].every((v) => asNum(v) != null);
  return hasGpci && asNum(gen.year) != null;
};

export interface IPayerGroup {
  key: string;
  payerId: number | null;
  payerName: string | null;
  recs: IDraftRecommendation[];
}

/**
 * Group recommendations by the payer they're for — a single contract often binds several payers (an HMA
 * network schedule honored by Aetna, Aetna Medicare Advantage, First Health), so grouping is what turns
 * "three identical rate calcs" into "one per payer". Payer-agnostic recs (base rate files, contract terms)
 * go in a "Contract-wide" group shown first; payer groups follow in first-appearance order; recs within a
 * group are in build order.
 */
export const groupByPayer = (recs: IDraftRecommendation[]): IPayerGroup[] => {
  const groups = new Map<string, IPayerGroup>();
  const order: string[] = [];
  recs.forEach((r) => {
    const { id, name } = recPayer(r);
    const key = id != null ? `p${id}` : "contract-wide";
    if (!groups.has(key)) {
      groups.set(key, { key, payerId: id, payerName: name, recs: [] });
      order.push(key);
    }
    const g = groups.get(key);
    if (g) g.recs.push(r);
  });
  const ordered = order.map((k) => groups.get(k) as IPayerGroup);
  const contractWide = ordered.filter((g) => g.key === "contract-wide");
  const payerGroups = ordered.filter((g) => g.key !== "contract-wide");
  return [...contractWide, ...payerGroups].map((g) => ({
    ...g,
    recs: forChecklist(g.recs),
  }));
};

/** Human label for a recommendation's review status. */
export const recStatusLabel = (status: string): string => {
  switch (status) {
    case "pending":
      return "Needs review";
    case "accepted":
      return "Accepted";
    case "overridden":
      return "Feedback given";
    case "rejected":
      return "Rejected";
    case "needs_input":
      return "Blocked";
    case "created":
      return "Created";
    case "superseded":
      return "Superseded";
    default:
      return status;
  }
};

/**
 * How to implement each recommendation type in the product UI — the "to-do" instruction shown on the
 * implementation checklist. Precise enough to act on; the values come from the proposal itself.
 */
const IMPLEMENTATION_INSTRUCTIONS: Record<string, string> = {
  contract_term:
    "On the Payer Rules page, create the Contract Fee Schedule for this provider × payer with the effective date shown (and any future-dated escalator terms).",
  base_rate_file:
    "Make sure the base rate file below is loaded (reuse the named one, or upload the generated CSV), then reference it from the rate calculation.",
  rate_calculation:
    "Add a Contract Rate Calculation to the contract fee schedule: the base rate file and rate percent shown, at the given sort order.",
  carveout:
    "Create a Contract Carveout for the listed codes/amounts (upload the CSV of CPT → fixed dollar).",
  lesser_of_charges:
    "Create a Lesser-of-Charges ruleset for this provider × payer at the multiplier shown.",
  charge_based_pricing:
    "Create a Charge-Based Pricing ruleset for the catch-all percentage-of-charges shown.",
  mpdr_ruleset:
    "Create a Multiple-Procedure Discount ruleset with the tier percentages shown.",
  case_rate_ruleset: "Create a Case-Rate ruleset for the bundle/group shown.",
  inpatient_ruleset:
    "Create an Inpatient ruleset (DRG base, weights, outlier/per-diem terms) as shown.",
  package_ruleset: "Create a Package ruleset for the grouping shown.",
  exploding_fee_schedule:
    "Create an Exploding Fee Schedule ruleset (percentage applied to the base rate file shown).",
  ma_payer_link:
    "This is a routing decision, not a single construct: if a separate Medicare Advantage payer is needed, create it first, then give it its own Contract Fee Schedule. Nothing here is created automatically.",
  provider_decomposition:
    "Scoping decision — confirm the provider entities involved; model each in its own run.",
  payer_consolidation:
    "Scoping decision — confirm which payers consolidate into this treatment.",
};

export const implementationInstruction = (componentType: string): string =>
  IMPLEMENTATION_INSTRUCTIONS[componentType] ??
  `Create the ${componentTypeLabel(componentType)} construct as shown.`;

/** Rejected recommendations don't belong on the to-do list. */
export const isOnChecklist = (rec: IDraftRecommendation): boolean =>
  rec.status !== "rejected";
